import type { RuoloUtente } from '@/types/database';
import { isStaffRole } from '@/types/database';

/**
 * Claims we read from the access JWT. Verification stays on the server; the
 * frontend only inspects the payload to decide what *not* to send (tenant
 * filters) so a client user cannot accidentally leak a `clienteId` query param.
 */
export interface AccessTokenClaims {
  sub: string;
  email?: string;
  ruolo?: RuoloUtente;
  cliente_id?: string | null;
  actor_kind?: 'client' | 'operator' | 'ingest';
  type?: string;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
  return atob(padded);
}

/** Best-effort decode. Mock tokens and malformed strings return `null`. */
export function decodeAccessToken(token: string | null | undefined): AccessTokenClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;

  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as AccessTokenClaims;
    if (typeof payload !== 'object' || payload === null) return null;
    return payload;
  } catch {
    return null;
  }
}

/** True when the current access token belongs to a tenant user, not staff. */
export function isTenantAccessToken(token: string | null | undefined): boolean {
  const claims = decodeAccessToken(token);
  if (!claims) return false;
  if (claims.actor_kind === 'operator') return false;
  if (claims.actor_kind === 'client') return true;
  return isTenantRoleFromClaims(claims.ruolo);
}

function isTenantRoleFromClaims(ruolo: RuoloUtente | undefined): boolean {
  if (!ruolo) return false;
  return !isStaffRole(ruolo);
}

const TENANT_QUERY_KEYS = new Set(['clienteId', 'cliente_id']);

/**
 * Drops tenant-scoping query params for client users. Staff keep them so the
 * tenant switcher and admin detail pages can filter. RLS still enforces the
 * actual isolation — this only stops the frontend from sending a redundant
 * (and potentially confusing) `clienteId`.
 */
export function stripTenantQueryParams(params: unknown): unknown {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return params;
  const next = { ...(params as Record<string, unknown>) };
  TENANT_QUERY_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}
